import React, { useRef } from 'react';
import { Box } from '@mui/material';
import { GanttFamily } from './ganttTypes';
import { barStyle } from './barStyle';
import { useCustomerColours } from './useCustomerColours';
import { TOKENS, DRAG_THRESHOLD_PX } from './ganttConfig';

const fmtDur = (min: number) => {
  const h = Math.floor(min / 60);
  const m = min % 60;
  return h && m ? `${h}h ${m}m` : h ? `${h}h` : `${m}m`;
};
const dueText = (d: number | null) => (d == null ? '—' : d === 0 ? 'Today' : d < 0 ? `${-d}d late` : `d+${d}`);
const dueTone = (d: number | null) => (d == null ? TOKENS.ink3 : d <= 0 ? TOKENS.bad : d <= 1 ? TOKENS.warn : TOKENS.ink3);

interface Props {
  fam: GanttFamily;
  dragging: boolean;
  onGrab: (ev: React.PointerEvent) => void;
  // Click the chip (a press-and-release that never crossed the drag threshold)
  // → open the held-family menu. Mirrors the click-vs-drag split on dock cards.
  onClickChip: (anchor: HTMLElement) => void;
}

// A held job/family in the Parking lane. Grab it (pointer-down) and drag up
// onto a press lane to schedule it; the held OTHER row is removed on drop.
// A plain click opens the menu instead — schedule it, or send the whole group
// back to Ready-to-Run.
export default function ParkedChip({ fam, dragging, onGrab, onClickChip }: Props) {
  const colourMap = useCustomerColours();
  const st = barStyle(fam.cust, fam.forme, colourMap);
  const jobCount = fam.jobs.length;

  // Only hand off to the drag once the pointer travels past the threshold, so a
  // plain click still opens the menu.
  const didDragRef = useRef(false);
  const onPointerDown = (ev: React.PointerEvent) => {
    if (ev.button !== 0) return;
    const sx = ev.clientX;
    const sy = ev.clientY;
    const start = ev;
    const move = (m: PointerEvent) => {
      if (Math.hypot(m.clientX - sx, m.clientY - sy) <= DRAG_THRESHOLD_PX) return;
      cleanup();
      didDragRef.current = true;
      onGrab(start);
    };
    const cleanup = () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', cleanup);
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', cleanup, { once: true });
  };

  return (
    <Box
      data-no-pan="1"
      onPointerDown={onPointerDown}
      onClick={(ev) => {
        if (didDragRef.current) { didDragRef.current = false; return; }
        onClickChip(ev.currentTarget);
      }}
      sx={{
        display: 'flex', alignItems: 'center', gap: 0.75, cursor: 'pointer', userSelect: 'none',
        background: TOKENS.panel, border: `1px solid ${TOKENS.line}`, borderRadius: `${TOKENS.radiusChip}px`,
        px: 0.75, py: 0.5, minWidth: 132, maxWidth: 188, boxShadow: TOKENS.shadowSm,
        opacity: dragging ? 0.4 : 1, transition: 'opacity .12s, box-shadow .12s',
        '&:hover': { boxShadow: TOKENS.shadowMd, borderColor: TOKENS.accent },
      }}
    >
      <Box sx={{ width: 4, alignSelf: 'stretch', borderRadius: 99, flexShrink: 0, background: st.fill, border: st.border ? `1px solid ${st.border}` : 'none' }} />
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5 }}>
          <Box sx={{ fontFamily: TOKENS.monoFont, fontWeight: 700, fontSize: 12, color: TOKENS.ink, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{fam.forme ?? '—'}</Box>
          <Box sx={{ fontSize: 10.5, fontWeight: 600, color: TOKENS.ink2, whiteSpace: 'nowrap' }}>{fam.cust}</Box>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.25 }}>
          {/* Union strips can run long on gang families; the chip is narrow, so
              cap at 6 swatches and fold the rest into a "+n". */}
          <Box sx={{ display: 'flex', gap: '2px', alignItems: 'center' }}>
            {fam.seps.slice(0, 6).map((s, i) => (
              <Box key={i} title={s.name ?? s.short} sx={{ width: 7, height: 8, borderRadius: '2px', background: s.hex, border: '0.5px solid rgba(0,0,0,.18)' }} />
            ))}
            {fam.seps.length > 6 && (
              <Box title={fam.seps.slice(6).map((s) => s.name ?? s.short).join(' · ')} sx={{ fontSize: 8.5, fontWeight: 700, color: TOKENS.ink3 }}>
                +{fam.seps.length - 6}
              </Box>
            )}
          </Box>
          <Box sx={{ fontFamily: TOKENS.monoFont, fontSize: 10, color: TOKENS.ink3, whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>
            {jobCount} job{jobCount === 1 ? '' : 's'} · {fmtDur(fam.setup + fam.run + fam.washup)}
          </Box>
        </Box>
      </Box>
      <Box sx={{ fontSize: 10.5, fontWeight: 600, color: dueTone(fam.earliestDue), whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums', flexShrink: 0 }}>{dueText(fam.earliestDue)}</Box>
    </Box>
  );
}
